import { ChevronDown, X } from "lucide-react";

import { Button } from "@/components/ui/button";

import {

  Collapsible,

  CollapsibleContent,

  CollapsibleTrigger,

} from "@/components/ui/collapsible";

import {

  Drawer,

  DrawerClose,

  DrawerContent,

  DrawerHeader,

  DrawerTitle,

} from "@/components/ui/drawer";

import { formatDateTime12 } from "@/utils/helpers";

import {

  CareHistoryAnswerDisplay,

  formatChangeAnswerForDisplay,

  getChangeTitle,

  getChangeValueLabel,

  getMeaningfulCarePlanChanges,

  normalizeChangeSide,

  type PlanHistoryRecord,

} from "./carePlanHistoryHelpers";



type Props = {

  open: boolean;

  onOpenChange: (open: boolean) => void;

  planHistory: PlanHistoryRecord | null;

};



export function CarePlanHistoryDrawer({ open, onOpenChange, planHistory }: Props) {

  const isCarePlan =

    planHistory?.carePlanType === "ccm" || planHistory?.carePlanType === "pcm";

  const title = isCarePlan ? "Care Plan History" : "Treatment Plan History";

  const editHistory = Array.isArray(planHistory?.editHistory) ? planHistory.editHistory : [];



  return (

    <Drawer

      direction="right"

      open={open && !!planHistory}

      onOpenChange={onOpenChange}

    >

      <DrawerContent className="flex h-dvh max-h-dvh min-h-0 min-w-[50vw]! w-[min(60vw,1400px)]! max-w-none! flex-col gap-0 rounded-none border-l px-0 pt-0">

        <DrawerHeader className="flex shrink-0 flex-row items-center justify-between gap-3 space-y-0 border-b px-4 py-3 text-left">

          <DrawerTitle className="text-lg">{title}</DrawerTitle>

          <DrawerClose asChild>

            <Button

              type="button"

              variant="ghost"

              size="icon"

              className="h-8 w-8 shrink-0"

              aria-label="Close drawer"

            >

              <X className="h-4 w-4" />

            </Button>

          </DrawerClose>

        </DrawerHeader>

        {editHistory.length > 0 ? (

          <div className="min-h-0 w-full flex-1 overflow-auto px-4 pb-6">

            <div className="space-y-4">

              {editHistory.map((h, index) => {

                const meaningfulChanges = getMeaningfulCarePlanChanges(h?.changes);

                return (

                  <div key={h._id || `hist-${index}`} className="space-y-1">

                    <div className="border-border mt-1 mb-0 pb-2">

                      <p className="text-foreground text-base font-semibold tracking-tight">

                        {h?.editedAt || h?.dateTime

                          ? formatDateTime12(h?.editedAt || h?.dateTime)

                          : "—"}

                      </p>

                      {(h?.editedBy?.name || h?.authorName) && (

                        <p className="text-muted-foreground mt-1 text-xs">

                          Updated by {h?.editedBy?.name || h?.authorName}

                        </p>

                      )}

                    </div>

                    <div className="space-y-2">

                      {meaningfulChanges.length > 0 ? (

                        meaningfulChanges.map((c, ci) => {

                          const prev = normalizeChangeSide(c.previous);

                          const curr = normalizeChangeSide(c.current);

                          const valueLabel = getChangeValueLabel(c.field);

                          const isQuestionChange =

                            c.field === "questions" || !!c.questionrandomid;



                          return (

                            <Collapsible

                              key={ci}

                              className="border-border bg-card/40 group rounded-lg border"

                            >

                              <CollapsibleTrigger className="hover:bg-muted/60 flex w-full items-start gap-2 rounded-lg px-3 py-3 text-left outline-none focus-visible:ring-2 focus-visible:ring-ring">

                                <div className="min-w-0 flex-1">

                                  <p className="text-foreground text-sm font-medium leading-snug">

                                    {getChangeTitle(c)}

                                  </p>

                                  {isQuestionChange && (curr.type || curr.category) && (

                                    <p className="text-muted-foreground mt-1 text-xs">

                                      {[curr.type, curr.category]

                                        .filter(Boolean)

                                        .join(" · ")}

                                    </p>

                                  )}

                                </div>

                                <ChevronDown className="text-muted-foreground mt-0.5 h-4 w-4 shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-180" />

                              </CollapsibleTrigger>

                              <CollapsibleContent className="overflow-hidden border-t border-border">

                                <div className="grid gap-3 p-3 sm:grid-cols-2">

                                  <div className="bg-muted/50 space-y-2 rounded-md border border-border/60 p-3">

                                    <p className="text-muted-foreground text-[11px] font-semibold uppercase tracking-wide">

                                      Previous

                                    </p>

                                    <div>

                                      <p className="text-muted-foreground text-xs font-medium">

                                        {valueLabel}

                                      </p>

                                      <div className="mt-1">

                                        <CareHistoryAnswerDisplay

                                          answer={formatChangeAnswerForDisplay(

                                            c.field,

                                            prev.answer,

                                          )}

                                        />

                                      </div>

                                    </div>

                                    {prev.comment ? (

                                      <div>

                                        <p className="text-muted-foreground text-xs font-medium">

                                          Comment

                                        </p>

                                        <p className="text-foreground mt-1 text-sm leading-relaxed">

                                          {prev.comment}

                                        </p>

                                      </div>

                                    ) : null}

                                  </div>

                                  <div className="bg-muted/50 space-y-2 rounded-md border border-border/60 p-3">

                                    <p className="text-muted-foreground text-[11px] font-semibold uppercase tracking-wide">

                                      Current

                                    </p>

                                    <div>

                                      <p className="text-muted-foreground text-xs font-medium">

                                        {valueLabel}

                                      </p>

                                      <div className="mt-1">

                                        <CareHistoryAnswerDisplay

                                          answer={formatChangeAnswerForDisplay(

                                            c.field,

                                            curr.answer,

                                          )}

                                        />

                                      </div>

                                    </div>

                                    {curr.comment ? (

                                      <div>

                                        <p className="text-muted-foreground text-xs font-medium">

                                          Comment

                                        </p>

                                        <p className="text-foreground mt-1 text-sm leading-relaxed">

                                          {curr.comment}

                                        </p>

                                      </div>

                                    ) : null}

                                  </div>

                                </div>

                              </CollapsibleContent>

                            </Collapsible>

                          );

                        })

                      ) : (

                        <p className="text-muted-foreground text-sm">

                          No field changes recorded for this update.

                        </p>

                      )}

                    </div>

                  </div>

                );

              })}

            </div>

          </div>

        ) : (

          <p className="text-muted-foreground shrink-0 px-4 pb-6 text-sm">

            No history available for this care plan.

          </p>

        )}

      </DrawerContent>

    </Drawer>

  );

}

